import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  ObservabilityK8sFormValues,
  ObservabilityLogsMode,
  OBSERVABILITY_K8S_INITIAL_VALUES,
} from './types';

interface ObservabilityDetectionRawData {
  expectedNamespace?: string;
  helmReleaseName?: string;
}

export function useKubernetesObservabilityInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): ObservabilityK8sFormValues {
  return useMemo(() => {
    if (!policy && templateData) {
      const d = templateData as Partial<ObservabilityK8sFormValues>;
      return {
        ...OBSERVABILITY_K8S_INITIAL_VALUES,
        description: (d.description as string) ?? '',
        oneUptimeURL:
          (d.oneUptimeURL as string) ||
          OBSERVABILITY_K8S_INITIAL_VALUES.oneUptimeURL,
        apiKey: '',
        projectID: (d.projectID as string) ?? '',
        mode: (d.mode as ObservabilityK8sFormValues['mode']) ?? 'connect-only',
        deploy: {
          ...OBSERVABILITY_K8S_INITIAL_VALUES.deploy,
          ...(d.deploy ?? {}),
          ingestionKey: '',
          autoProvisionIngestionKey:
            (d.deploy as Partial<ObservabilityK8sFormValues['deploy']>)
              ?.autoProvisionIngestionKey ?? false,
          preset:
            (d.deploy as Partial<ObservabilityK8sFormValues['deploy']>)
              ?.preset ?? 'standard',
          logsMode:
            ((d.deploy as Partial<ObservabilityK8sFormValues['deploy']>)
              ?.logsMode as ObservabilityLogsMode) ?? '',
        },
        detection: {
          ...(() => {
            const det = d.detection as
              | ObservabilityDetectionRawData
              | undefined;
            return {
              expectedNamespace: det?.expectedNamespace ?? '',
              helmReleaseName: det?.helmReleaseName ?? '',
            };
          })(),
        },
        tlsSkipVerify: (d.tlsSkipVerify as boolean) ?? false,
        autoRotateKey: (d.autoRotateKey as boolean) ?? false,
        connectionVerified: false,
      };
    }

    if (!policy) {
      return OBSERVABILITY_K8S_INITIAL_VALUES;
    }

    const d = (policy.Data ?? {}) as Partial<ObservabilityK8sFormValues>;
    const deployData = d.deploy as
      | Partial<ObservabilityK8sFormValues['deploy']>
      | undefined;
    const detectionData = d.detection as
      | ObservabilityDetectionRawData
      | undefined;

    return {
      name: policy.Name,
      description: (d.description as string) ?? '',
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      oneUptimeURL: (d.oneUptimeURL as string) ?? '',
      apiKey: '',
      projectID: (d.projectID as string) ?? '',
      mode: (d.mode as ObservabilityK8sFormValues['mode']) ?? 'connect-only',
      deploy: {
        ingestionKey: '',
        autoProvisionIngestionKey:
          deployData?.autoProvisionIngestionKey ?? false,
        agentURL: deployData?.agentURL ?? '',
        targetNamespace: deployData?.targetNamespace ?? 'oneuptime-agent',
        helmReleaseName: deployData?.helmReleaseName ?? 'kubernetes-agent',
        preset: deployData?.preset ?? 'standard',
        logsMode: deployData?.logsMode ?? '',
      },
      detection: {
        expectedNamespace: detectionData?.expectedNamespace ?? '',
        helmReleaseName: detectionData?.helmReleaseName ?? '',
      },
      tlsSkipVerify: (d.tlsSkipVerify as boolean) ?? false,
      autoRotateKey: (d.autoRotateKey as boolean) ?? false,
      connectionVerified: false,
    };
  }, [policy, templateData]);
}
